import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchPublicQuestion, submitShareAnswer } from '../lib/api';
import { motion, AnimatePresence, springs, Pressable, FadeIn, Skeleton, Confetti } from '../lib/motion';
import { haptic } from '../lib/haptics';

const REFERRAL_KEY = 'squishy_pending_referral';

function getFingerprint(): string {
  const key = 'squishy_fp';
  let fp = localStorage.getItem(key);
  if (!fp) {
    fp = crypto.randomUUID();
    localStorage.setItem(key, fp);
  }
  return fp;
}

type QuestionData = Awaited<ReturnType<typeof fetchPublicQuestion>>;
type AnswerResult = Awaited<ReturnType<typeof submitShareAnswer>>;

/** Brand backdrop shared by every state of this public page. */
const pageBg = {
  background: 'linear-gradient(168deg, #1e1b4b 0%, #312e81 48%, #134e4a 100%)',
} as const;

function BrandHeader({ subtitle }: { subtitle?: string }) {
  return (
    <div className="mb-5 flex items-center gap-3">
      <img
        src="/logo.png"
        alt=""
        className="h-11 w-11"
        style={{ filter: 'drop-shadow(0 8px 16px rgba(0, 0, 0, 0.3))' }}
      />
      <div>
        <p className="font-display text-base font-semibold tracking-wide text-white">Squishy School</p>
        {subtitle && <p className="text-xs font-medium text-white/50">{subtitle}</p>}
      </div>
    </div>
  );
}

function SignUpCta({ heading, body }: { heading: string; body: string }) {
  return (
    <FadeIn delay={0.15}>
      <div className="mt-6 rounded-3xl bg-white/[0.08] p-6 text-center ring-1 ring-white/10 backdrop-blur">
        <p className="font-display text-lg font-semibold text-white">{heading}</p>
        <p className="mx-auto mt-1.5 max-w-xs text-[13px] leading-relaxed text-white/60">{body}</p>
        <Link
          to="/signup"
          className="mt-4 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-squishy-gold px-8 text-sm font-bold text-squishy-navy shadow-pop transition hover:brightness-105 active:scale-[0.98]"
        >
          Sign up free
        </Link>
        <p className="mt-2.5 text-[11px] text-white/40">Free to start. Available on iOS, Android, and the web.</p>
      </div>
    </FadeIn>
  );
}

export function SharedQuestionPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [question, setQuestion] = useState<QuestionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSata = question?.question_type === 'sata';
  const isFlashcard = question?.question_type === 'flashcard';
  const [flashcardFlipped, setFlashcardFlipped] = useState(false);

  useEffect(() => {
    if (!shareId) return;
    localStorage.setItem(REFERRAL_KEY, shareId);
    fetchPublicQuestion(shareId)
      .then((q) => {
        setQuestion(q);
      })
      .catch(() => setError('Question not found or link has expired.'))
      .finally(() => setLoading(false));
  }, [shareId]);

  const toggleOption = (i: number) => {
    if (result) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (isSata) {
        if (next.has(i)) next.delete(i);
        else next.add(i);
      } else {
        next.clear();
        next.add(i);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!shareId) return;
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      const answer = { chosen_index: isSata ? [...selected].sort() : [...selected][0]! as number };
      const res = await submitShareAnswer(shareId, answer, getFingerprint());
      setResult(res);
      if (res.correct) haptic.success(); else haptic.error();
    } catch {
      setError('Failed to submit answer. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col p-4" style={pageBg}>
        <div
          className="mx-auto w-full max-w-lg flex-1"
          style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}
        >
          <BrandHeader subtitle="Loading question" />
          <div className="rounded-3xl bg-white p-6 shadow-2xl">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="mt-4 h-5 w-full" />
            <Skeleton className="mt-2 h-5 w-3/4" />
            <div className="mt-6 space-y-2.5">
              <Skeleton className="h-[52px] w-full" />
              <Skeleton className="h-[52px] w-full" />
              <Skeleton className="h-[52px] w-full" />
              <Skeleton className="h-[52px] w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !question) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4" style={pageBg}>
        <FadeIn className="w-full max-w-md">
          <div className="rounded-3xl bg-white p-8 text-center shadow-2xl">
            <img src="/logo.png" alt="Squishy School" className="mx-auto h-16 w-16" />
            <h1 className="mt-4 text-lg font-bold text-squishy-navy">Link unavailable</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">{error ?? 'Question not found.'}</p>
            <p className="mt-1 text-xs text-slate-400">You can still explore thousands of practice questions inside the app.</p>
            <Link
              to="/signup"
              className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-xl bg-squishy-tealDark px-8 text-sm font-semibold text-white shadow-pop transition hover:brightness-110 active:scale-[0.98]"
            >
              Create a free account
            </Link>
          </div>
        </FadeIn>
      </div>
    );
  }

  // Flashcard: completely separate render
  if (isFlashcard && question) {
    return (
      <div className="flex min-h-screen flex-col p-4" style={pageBg}>
        <div
          className="mx-auto w-full max-w-lg flex-1"
          style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <BrandHeader subtitle="Flashcard" />

          {/* Flashcard */}
          <FadeIn>
            <AnimatePresence mode="wait" initial={false}>
              {!flashcardFlipped ? (
                <motion.div
                  key="front"
                  initial={{ opacity: 0, rotateX: -10 }}
                  animate={{ opacity: 1, rotateX: 0 }}
                  exit={{ opacity: 0, rotateX: 10 }}
                  transition={springs.soft}
                  onClick={() => !flashcardFlipped && setFlashcardFlipped(true)}
                  className="min-h-[320px] cursor-pointer rounded-3xl bg-white/[0.07] shadow-2xl ring-1 ring-white/15 backdrop-blur transition active:scale-[0.99]"
                >
                  <div className="flex min-h-[320px] flex-col items-center justify-center p-7">
                    {question.topic && (
                      <span className="mb-4 rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/70">
                        {question.topic}
                      </span>
                    )}
                    <p className="text-center text-lg font-semibold leading-relaxed text-white">
                      {question.front}
                    </p>
                    <div className="mt-7 flex items-center gap-1.5 text-xs text-white/50">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      Tap to reveal answer
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="back"
                  initial={{ opacity: 0, rotateX: -10 }}
                  animate={{ opacity: 1, rotateX: 0 }}
                  exit={{ opacity: 0, rotateX: 10 }}
                  transition={springs.soft}
                  className="min-h-[320px] rounded-3xl bg-white shadow-2xl ring-1 ring-black/5"
                >
                  <div className="min-h-[320px] p-7">
                    {question.topic && (
                      <span className="mb-3 inline-block rounded-full bg-teal-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-teal-700">
                        {question.topic}
                      </span>
                    )}
                    <p className="mb-2 text-xs font-medium text-slate-400">Q: {question.front}</p>
                    <div className="border-t border-slate-100 pt-4">
                      <p className="whitespace-pre-line text-[15px] leading-relaxed text-slate-800">
                        {question.back}
                      </p>
                    </div>
                    {question.category && (
                      <div className="mt-4 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                        {question.category}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </FadeIn>

          {!flashcardFlipped && (
            <Pressable
              onClick={() => setFlashcardFlipped(true)}
              className="mt-4 min-h-[48px] w-full rounded-xl bg-white/15 text-sm font-semibold text-white ring-1 ring-white/20 backdrop-blur transition hover:bg-white/25"
            >
              Show answer
            </Pressable>
          )}

          {/* CTA */}
          <SignUpCta
            heading="Turn cramming into winning"
            body="Unlimited flashcards, practice questions, and live study group battles for nursing, medical, PA, and law exams."
          />
        </div>
      </div>
    );
  }

  const correctIndices: number[] = result && result.correct_index !== null && result.correct_index !== undefined
    ? Array.isArray(result.correct_index) ? result.correct_index : [result.correct_index]
    : [];

  return (
    <div className="flex min-h-screen flex-col p-4" style={pageBg}>
      <div
        className="mx-auto w-full max-w-lg flex-1"
        style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <BrandHeader subtitle={question.exam_name ? (question.exam_code ?? question.exam_name) : 'Practice question'} />

        {/* Question card */}
        <FadeIn>
          <div className="relative rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-black/5">
            {result?.correct && <Confetti count={40} />}
            <div className="mb-3 flex items-center gap-2">
              {question.topic && (
                <span className="rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-semibold text-teal-700">
                  {question.topic}{question.subtopic ? ` / ${question.subtopic}` : ''}
                </span>
              )}
              {isSata && (
                <span className="ml-auto rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-700">
                  Select All That Apply
                </span>
              )}
            </div>

            <h2 className="whitespace-pre-wrap text-[15px] font-semibold leading-relaxed text-slate-900">
              {question.question}
            </h2>

            {/* Options */}
            <div className="mt-5 space-y-2.5">
              {(question.options ?? []).map((opt, i) => {
                    const letter = String.fromCharCode(65 + i);
                    const isSelected = selected.has(i);
                    let optClass = 'border-slate-200 bg-white hover:border-squishy-teal hover:bg-teal-50/40';

                    if (result) {
                      if (correctIndices.includes(i)) {
                        optClass = 'border-emerald-400 bg-emerald-50';
                      } else if (isSelected && !correctIndices.includes(i)) {
                        optClass = 'border-red-400 bg-red-50';
                      } else {
                        optClass = 'border-slate-100 bg-slate-50 opacity-60';
                      }
                    } else if (isSelected) {
                      optClass = 'border-squishy-tealDark bg-teal-50 ring-1 ring-squishy-tealDark';
                    }

                    const badgeClass = result && correctIndices.includes(i)
                      ? 'bg-emerald-500 text-white'
                      : result && isSelected && !correctIndices.includes(i)
                      ? 'bg-red-500 text-white'
                      : isSelected
                      ? 'bg-squishy-tealDark text-white'
                      : 'bg-slate-100 text-slate-600';

                    return (
                      <Pressable
                        key={i}
                        disabled={!!result}
                        onClick={() => toggleOption(i)}
                        className={`flex min-h-[52px] w-full items-start gap-3 rounded-2xl border-2 p-3.5 text-left transition-colors ${optClass}`}
                      >
                        <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center ${isSata ? 'rounded-md' : 'rounded-full'} text-xs font-bold transition-colors ${badgeClass}`}>
                          {result && correctIndices.includes(i) ? '✓' :
                           result && isSelected ? '✗' :
                           isSelected && isSata ? '✓' : letter}
                        </span>
                        <span className="text-sm leading-relaxed text-slate-800">{opt}</span>
                      </Pressable>
                    );
                  })}
            </div>

            {/* Submit button */}
            {!result && (
              <Pressable
                haptic="medium"
                onClick={handleSubmit}
                disabled={selected.size === 0 || submitting}
                className="mt-5 min-h-[52px] w-full rounded-xl bg-gradient-to-r from-squishy-tealDark to-squishy-teal text-sm font-semibold text-white shadow-pop transition hover:shadow-glow disabled:opacity-40 disabled:shadow-none"
              >
                {submitting ? 'Checking...' : 'Submit answer'}
              </Pressable>
            )}

            {/* Result */}
            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={springs.soft}
                  className={`mt-5 rounded-2xl border p-4 ${result.correct ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}
                >
                  <p className={`flex items-center gap-2 text-[15px] font-bold ${result.correct ? 'text-emerald-700' : 'text-red-700'}`}>
                    <motion.span
                      initial={{ scale: 0, rotate: -30 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={springs.bouncy}
                      className="inline-flex"
                    >
                      {result.correct ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </motion.span>
                    {result.correct ? 'Nailed it!' : 'So close!'}
                  </p>
                  {result.brief_explanation && (
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">{result.brief_explanation}</p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </FadeIn>

        {/* CTA */}
        <SignUpCta
          heading={result ? (result.correct ? 'You are on a roll!' : 'Ready for a rematch?') : 'Want more questions like this?'}
          body="Unlimited practice questions, flashcards, and live study group battles for nursing, medical, PA, and law exams."
        />
      </div>
    </div>
  );
}
